<template>
    <div
        ref="rootMenu"
        class="mobile-nav"
        :inert="$bp.bps.isLargeDesktop"
    >
        <div class="controls">
            <transition name="back">
                <button v-if="selected[0]" class="btn back" @click="selected = []">
                    <DSIcon code="arrow-left" size="l" />
                </button>
            </transition>
            <button class="btn close" @click="layout.sidebars.right = false">
                <DSIcon
                    code="st1website-fill/close"
                    size="xxl"
                />
            </button>
        </div>
        <transition-group
            tag="div"
            name="panels"
            class="panels"
            :class="{
                [panelTransitionClass]: true,
            }"
            @after-leave="panelLeft"
            @before-leave="panelLeave"
        >
            <div v-if="!selected.length" key="panel-level-0" class="panel panel-0">
                <ul class="root-level">
                    <li
                        v-for="(item, key) in links"
                        :key="`link-${item.id}-${key}`"
                    >
                        <nuxt-link
                            :class="{
                                'selected': route.path.includes(`/${item.url}`),
                            }"
                            :to="`/${item.url}`"
                            @click="closeNav"
                        >
                            {{ item.text }}
                        </nuxt-link>
                    </li>
                </ul>
                <LayoutUserMenu v-if="userMenu" class="theme-white">
                    <template #user-icon><i></i></template>
                </LayoutUserMenu>
                <LayoutSearch />
                <ul class="nav level-0">
                    <li
                        v-for="(item, key) in navigationOne"
                        :key="`item-${item.id}-${key}`"
                    >
                        <nuxt-link :to="`/${item.url}`" @click="closeNav">{{ item.text }}</nuxt-link>
                        <button v-if="item.children.length" @click="selected = [item.id]">
                            <DSIcon
                                code="st1website-fill/arrow-right"
                                size="l"
                            />
                        </button>
                    </li>
                </ul>
                <div v-if="$paths.getPath('stationLocator') !== '/'" class="additional-links">
                    <nuxt-link :to="$paths.getPath('contact')" @click="closeNav">
                        <span class="bg"><DSIcon code="message" size="l" /></span>
                        <span>{{ $i18n.t('links.contact') }}</span>
                    </nuxt-link>
                    <nuxt-link :to="$paths.getPath('stationLocator')" @click="closeNav">
                        <span class="bg"><DSIcon code="location" size="l" /></span>
                        <span>{{ $i18n.t('links.stationLocator') }}</span>
                    </nuxt-link>
                </div>
            </div>
            <div v-if="subPanel" key="panel-level-1" class="panel sub">
                <div class="illustration">
                    <DSFrontifyAsset
                        v-if="subPanel.navigationImage"
                        class="sub-nav-illustration"
                        :asset="subPanel.navigationImage"
                    />
                    <h3 class="h2">{{ subPanel.text }}</h3>
                </div>
                <ul class="nav level-1">
                    <li
                        v-for="(item, key) in subPanel.children"
                        :key="`sub-item-${item.id}-${key}`"
                    >
                        <nuxt-link :to="`/${item.url}`" @click="closeNav">{{ item.text }}</nuxt-link>
                    </li>
                </ul>
                <div v-if="$paths.getPath('stationLocator') !== '/'" class="additional-links">
                    <nuxt-link :to="$paths.getPath('stationLocator')" @click="closeNav">
                        <span class="bg"><DSIcon code="st1website-line/location" size="l" /></span>
                        <span>{{ $i18n.t('links.stationLocator') }}</span>
                    </nuxt-link>
                </div>
            </div>
        </transition-group>
    </div>
</template>
<script setup>
import { ref, computed, provide, watch, inject, useRoute, useNuxtApp, useI18n } from '#imports';
import { useSwipe } from '@vueuse/core';

const { $i18n, $paths, $bp } = useNuxtApp();
const route = useRoute();
const selected = ref([]);
const layout = inject('layout');
const panelTransitionClass = ref('');
const rootMenu = ref(null);

useSwipe(rootMenu, {
    onSwipeEnd: (event, direction) => {
        if (direction === 'right') {
            if (selected.value.length === 1) {
                selected.value = [];
            } else {
                closeNav();
            }
        }
    },
});

provide('selected', selected);

const props = defineProps({
    links: {
        type: Array,
        required: false,
        default: () => [],
    },
    userMenu: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const routeArray = computed(() => {
    const routeArray = route.path.split('/');

    return routeArray.filter(value => value !== '' && value !== 'preview');
});

const navigationOne = computed(() => {
    if (!props.links?.map(item => item.url).includes(routeArray.value[0])) {
        return props.links?.[0]?.children ?? [];
    }

    return props.links.find(item => item.url === routeArray.value[0])?.children;
});
const subPanel = computed(() => navigationOne.value?.find(item => item.id === selected.value[0]));

watch(() => selected.value, (to, from) => {
    if (to.length > from.length) {
        panelTransitionClass.value = 'leave-to-left';
    } else {
        panelTransitionClass.value = 'enter-from-left';
    }
});

const panelLeave = el => {
    const { width } = el.getBoundingClientRect();
    Object.assign(el.style, {
        width: `${width}px`,
    });
};

const panelLeft = () => {
    panelTransitionClass.value = '';
};

const closeNav = () => {
    layout.value.sidebars = {
        left: false,
        right: false,
    };
};

useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            links: {
                contact: 'Contact us',
                stationLocator: 'Nearest station',
            },
        },
        'fi-FI': {
            links: {
                contact: 'Ota yhteyttä',
                stationLocator: 'Lähin asema',
            },
        },
        'sv-SE': {
            links: {
                contact: 'Kontakta oss',
                stationLocator: 'Närmaste station',
            },
        },
        'no-NO': {
            links: {
                contact: 'Kontakt oss',
                stationLocator: 'Nærmeste stasjon',
            },
        },
    },
});
</script>
<style lang="scss" scoped>
button {
    -webkit-tap-highlight-color: transparent;
}

.mobile-nav {
    overflow-x: hidden;
    display: grid;
    grid-template-rows: repeat(3, auto) 1fr auto;
    min-height: 100%;
}

.illustration {
    display: flex;
    flex-flow: column nowrap;
    border-top: 2px solid var(--color-base-grey-300);
    padding-top: 1.125rem;

    .image-container {
        width: 100%;
        padding-top: 36.5%;
        border-radius: 1rem;
    }
}

.back-enter-active, .back-leave-active {
    transition: transform .5s cubic-bezier(.19, .73, .23, 1);
}

.back-enter-from {
    opacity: 0;
    transform: translateY(1rem);
}

.back-leave-to {
    opacity: 0;
    transform: translateY(1rem);
}

.panels {
    padding: 0 1.5rem;

    &.leave-to-left .panels-leave-to, &.enter-from-left .panels-enter-from {
        transform: translateX(calc(-100% - 1.5rem));
    }
}


.panels-enter-active, .panels-leave-active {
    transition: transform .5s cubic-bezier(.19, .73, .23, 1);
}

.panels-enter-from {
    transform: translateX(calc(100% + 1.5rem));
}

.panels-leave-to {
    position: absolute;
    transform: translateX(calc(100% + 1.5rem));
}

.nav {
    list-style: none;
    margin: 0;
    padding: 0;
}

.panel {
    display: flex;
    flex-flow: row wrap;

    &.sub {
        .h2 {
            font-weight: 600;
        }
    }

    > *, > ul {
        width: 100%;
        padding-bottom: 1.125rem;
        border-bottom: none;

        &:not(:nth-child(3)) {
            border-bottom: 2px solid var(--color-base-grey-300);
            margin-bottom: 1.125rem;
        }
    }
}

.level-0 {
    li {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;
    }

    a {
        display: inline-flex;
        font-size: 1.75rem;
        line-height: 1;
        padding: 1.375rem 1.5rem 1.375rem 0;
        font-weight: 600;
    }

    .icon {
        transition: all .75s cubic-bezier(.77, 1.34, .63, 1);
        opacity: 1;
        color: var(--color-base-grey-500);
    }

    &.selected {
        a {
            padding-top: 0;
        }

        .icon {
            pointer-events: none;
            opacity: 0;
        }
    }
}

.level-1 {
    li:not(:last-child) {
        margin-bottom: 1rem;
    }

    a {
        display: inline-flex;
        font-size: 1.375rem;
        line-height: 1;
    }
}

.additional-links {
    a {
        display: flex;
        font-size: 1.125rem;
        align-items: center;

        .bg {
            width: 4rem;
            height: 4rem;
            background: var(--color-base-grey-200);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            margin-right: .875rem;
        }

        &:not(:last-child) {
            margin-bottom: .5rem;
        }
    }
}

.controls {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    margin-bottom: 2rem;
    padding: 1.5rem 1.5rem 0;

    .btn {
        display: inline-flex;
        align-items: center;

        &.close {
            margin-left: auto;
        }
    }
}

.root-level {
    display: flex;
    flex-flow: row wrap;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: inline-flex;

        &:not(:last-child) {
            margin-right: .5rem;
        }
    }

    a {
        display: inline-flex;
        align-items: center;
        padding: .5rem .75rem;
        border-width: 1px;
        border-style: solid;
        border-color: transparent;
        border-radius: 1.25rem;
        letter-spacing: .025em;

        &.selected {
            border-color: var(--color-secondary-50);
            background: var(--color-secondary-50);
        }
    }
}

.user {
    width: 100%;
}

:deep(.user .btn-user) {
    border-radius: 2rem;

    i {
        display: none;
    }
}
</style>
